import { useEffect, useState } from 'react';
import { getFeedTransports } from '@/hooks/useHydra';

// Live feed transport — CLIENT-SIDE (this browser/tab's hydra WS), polled from
// the useHydra singleton. The headline diagnostic for the "every reload/window
// forms a new connection" report: SharedWorker = one upstream shared across all
// this browser's tabs; direct = a separate WS per window (the fallback where
// `type:'module'` SharedWorkers aren't supported, e.g. Safari). Reflects only the
// browser you're viewing this in — load it where the symptom is seen.
function transportBadge(t: 'shared' | 'direct' | 'connecting') {
  if (t === 'shared') return { cls: 'bg-green-600/80 text-white', label: 'SharedWorker' };
  if (t === 'direct') return { cls: 'bg-amber-500/80 text-white', label: 'direct WS' };
  return { cls: 'bg-muted text-muted-foreground', label: 'connecting' };
}
export function FeedTransport() {
  const [feeds, setFeeds] = useState(getFeedTransports);
  useEffect(() => {
    const id = setInterval(() => setFeeds(getFeedTransports()), 2000);
    return () => clearInterval(id);
  }, []);
  const anyDirect = feeds.some((f) => f.transport === 'direct');
  const allShared = feeds.length > 0 && feeds.every((f) => f.transport === 'shared');
  return (
    <section className="border-border rounded-lg border p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h2 className="text-foreground text-sm font-semibold">Live feed transport</h2>
        {feeds.length > 0 && (
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
              anyDirect ? 'bg-amber-500/80 text-white' : allShared ? 'bg-green-600/80 text-white' : 'bg-muted text-muted-foreground'
            }`}
          >
            {anyDirect ? 'DIRECT — one WS per window' : allShared ? 'SHARED — one WS across tabs' : 'CONNECTING'}
          </span>
        )}
        <span className="text-muted-foreground text-[11px]">
          this browser&apos;s hydra feed · SharedWorker shares one upstream across all your tabs; a direct fallback
          (Safari / unsupported) opens a separate socket per window — the &ldquo;new connection every reload/window&rdquo; symptom
        </span>
      </div>
      {feeds.length === 0 ? (
        <p className="text-muted-foreground py-3 text-center text-xs">
          No feed client on this tab yet — still connecting, or the desk shell isn&apos;t mounted here.
        </p>
      ) : (
        <table className="w-full text-left text-xs font-mono">
          <thead className="text-muted-foreground border-border border-b">
            <tr>
              <th className="py-1.5 pr-3 font-medium">Feed</th>
              <th className="px-3 font-medium">Role</th>
              <th className="px-3 font-medium">Transport</th>
              <th className="px-3 text-center font-medium" title="upstream WS connected">Live</th>
              <th className="px-3 font-medium">Upstream</th>
            </tr>
          </thead>
          <tbody>
            {feeds.map((f) => {
              const t = transportBadge(f.transport);
              return (
                <tr key={f.id} className="border-border/50 border-b">
                  <td className="text-foreground py-1.5 pr-3">{f.id}</td>
                  <td className="text-muted-foreground px-3">{f.role}</td>
                  <td className="px-3">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${t.cls}`}>{t.label}</span>
                  </td>
                  <td className="px-3 text-center">
                    {f.connected ? <span className="text-green-400">●</span> : <span className="text-muted-foreground">○</span>}
                  </td>
                  <td className="text-muted-foreground max-w-[280px] truncate px-3" title={f.url}>
                    {f.url}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
